import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Clock, MailX, Zap } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";

const MODES = [
  {
    value: "instant",
    icon: Zap,
    label: "Instant",
    blurb: "Email every event as it happens. Best for active deals and hot leads.",
  },
  {
    value: "digest",
    icon: Clock,
    label: "Daily digest",
    blurb: "One consolidated email per day. Pick a time that fits your routine.",
  },
  {
    value: "in_app_only",
    icon: MailX,
    label: "In-app only",
    blurb: "No emails — see everything in the notification bell + sidemenu badges.",
  },
];

const CATEGORY_LABELS = {
  deal_rooms: "Deal Room activity",
  messages: "Direct messages",
  queries: "Queries & replies",
  verification: "KYC & verification",
  interests: "Interests & watchlist",
  diligence: "Diligence updates",
  onboarding: "Onboarding requests",
  investors: "Investor updates",
  startups: "Startup updates",
  funding: "Funding profile events",
  profile: "Profile snapshots",
  other: "Everything else",
};

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/user/notification-prefs")
      .then((r) => setPrefs(r.data))
      .catch(() => toast.error("Could not load notification preferences"));
  }, []);

  const patch = (next) => setPrefs((p) => ({ ...p, ...next }));
  const patchCategory = (key, value) =>
    setPrefs((p) => ({ ...p, categories: { ...(p?.categories || {}), [key]: value } }));

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const { data } = await api.put("/user/notification-prefs", {
        mode: prefs.mode,
        digest_hour_ist: prefs.digest_hour_ist,
        categories: prefs.categories,
      });
      setPrefs(data);
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return (
      <DashboardShell title="Notification Settings">
        <Card><div className="text-sm text-ink-muted">Loading…</div></Card>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Notification Settings" subtitle="Choose how VenturaZ reaches you when a deal room, KYC check, or message needs your attention.">
      <div className="space-y-6 max-w-3xl">
        <Card testId="notif-prefs-delivery">
          <div className="overline flex items-center gap-1.5 mb-3"><Bell size={12} className="text-gold" /> Delivery mode</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = prefs.mode === m.value;
              return (
                <button
                  key={m.value}
                  onClick={() => patch({ mode: m.value })}
                  className={`text-left rounded-md border p-4 transition-all ${
                    active
                      ? "border-gold bg-gold/[0.06] shadow-[0_0_0_1px_rgba(138,102,0,0.15)]"
                      : "border-navy/15 hover:border-gold/50 bg-white/40"
                  }`}
                  data-testid={`notif-mode-${m.value}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={14} className={active ? "text-gold" : "text-ink-muted"} />
                    <span className="font-heading text-ink">{m.label}</span>
                    {active && <Pill tone="gold">Selected</Pill>}
                  </div>
                  <div className="text-xs text-ink-soft mt-2 leading-relaxed">{m.blurb}</div>
                </button>
              );
            })}
          </div>
          {prefs.mode === "digest" && (
            <div className="mt-4 flex items-center gap-3">
              <label className="text-xs text-ink-soft font-heading">Digest arrives at</label>
              <select
                value={prefs.digest_hour_ist ?? 9}
                onChange={(e) => patch({ digest_hour_ist: parseInt(e.target.value, 10) })}
                className="border border-navy/20 rounded px-2 py-1 text-sm bg-white"
                data-testid="notif-digest-hour"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, "0")}:00 IST</option>
                ))}
              </select>
              <span className="text-[11px] text-ink-muted italic">
                One consolidated email per day at this hour.
              </span>
            </div>
          )}
        </Card>

        <Card testId="notif-prefs-categories">
          <div className="overline mb-1">Category filters</div>
          <div className="text-xs text-ink-soft mb-4">
            In-app notifications are always on. Toggle a category off to skip its emails.
          </div>
          <div className="space-y-2">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const on = prefs.categories?.[key] !== false;
              return (
                <div key={key} className="flex items-center justify-between py-2 border-b border-navy/5 last:border-0">
                  <div className="text-sm text-ink">{label}</div>
                  <button
                    onClick={() => patchCategory(key, !on)}
                    className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${
                      on ? "bg-emerald-600" : "bg-navy/20"
                    }`}
                    data-testid={`notif-cat-${key}`}
                    aria-label={`${label} · ${on ? "on" : "off"}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                        on ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="notif-save-btn">
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </div>
    </DashboardShell>
  );
}
